import type { OnInit } from '@angular/core';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import type { ProgressiveFormGroupState } from '@innogy/progressive-ngrx-forms';
import type { KeyValue } from 'ngrx-forms';
import { TrackToolStep } from '@analytics/models';
import { ViewportScroller, NgTemplateOutlet } from '@angular/common';
import { ScrollService } from '@innogy/utils-deprecated';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TrackStepDirective } from '@analytics/events';
import { NgrxFormsModule } from 'ngrx-forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressiveNgrxFormsModule } from '@innogy/progressive-ngrx-forms';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CSCaptureDirective } from '@legacy/utils-contentsquare';
import { GenericRichTextDirective } from '@core/jss-utils';

import { SimpleProgressiveFormsConfigService } from '../../services/simple-progressive-forms-config.service';
import { SimpleProgressiveFormCardContent } from '../../models/simple-progressive-form-card.model';
import type { SimpleProgressiveFormSubmitContext } from '../../models/simple-progressive-form-card.model';
import { FocusOnFirstChildControlDirective } from '../../directives/focus-on-first-child-control.directive';
import { ShowProgressiveFormsIconPipe } from '../../pipes/show-progressive-forms-icon.pipe';
import { ShowProgressiveFormsEditButtonPipe } from '../../pipes/show-progressive-forms-edit-button.pipe';
import { ShowProgressiveFormsSeparatorPipe } from '../../pipes/show-progressive-forms-separator.pipe';
import { ShowProgressiveFormsSummaryPipe } from '../../pipes/show-progressive-forms-summary.pipe';
import { ShowProgressiveFormsFormPipe } from '../../pipes/show-progressive-forms-form.pipe';

@Component({
  selector: 'wl-simple-progressive-form-card',
  templateUrl: './simple-progressive-form-card.component.html',
  styleUrls: [
    './simple-progressive-form-card.component.essent.scss',
    './simple-progressive-form-card.component.ed.scss',
  ],
  standalone: true,
  imports: [
    ProgressiveNgrxFormsModule,
    AutomationIdDirective,
    CSCaptureDirective,
    FaIconComponent,
    GenericRichTextDirective,
    NgTemplateOutlet,
    NgbCollapse,
    TrackStepDirective,
    NgrxFormsModule,
    FocusOnFirstChildControlDirective,
    TranslatePipe,
    ShowProgressiveFormsIconPipe,
    ShowProgressiveFormsEditButtonPipe,
    ShowProgressiveFormsSeparatorPipe,
    ShowProgressiveFormsSummaryPipe,
    ShowProgressiveFormsFormPipe,
  ],
})
export class SimpleProgressiveFormCardComponent<TValue extends KeyValue>
  implements OnInit
{
  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly config: SimpleProgressiveFormsConfigService,
    private readonly viewportScroller: ViewportScroller,
    private readonly scrollService: ScrollService
  ) {}

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() progressiveFormState?: ProgressiveFormGroupState<TValue> | null;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() content?: SimpleProgressiveFormCardContent;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackingConfig?: TrackToolStep;

  /**
   * When set to true, the component will go into `async` mode, meaning
   * that an action is dispatched to mark the state as having pending actions.
   * As soon as the state is marked to have no more pending actions, the submission will continue.
   * Useful in cases where we want to wait for async validation or API calls to finish before submitting and
   * passing the active flag to another form step.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() asyncSubmission = false;

  @Output() submitted = new EventEmitter<SimpleProgressiveFormSubmitContext>();

  @ContentChild('summary', { static: true }) summaryRef?: TemplateRef<any>;
  @ContentChild('content', { static: true }) contentRef?: TemplateRef<any>;
  @ContentChild('footnote', { static: true }) footnoteRef?: TemplateRef<any>;

  @ViewChild('formRef') formRef!: TemplateRef<any>;

  enableAutomaticFocusing = this.config.enableAutomaticFocusing;
  totalSteps = this.config.totalStepsInFlow;

  ngOnInit(): void {
    if (!this.progressiveFormState) {
      throw new Error(
        'No progressive form state provided to progressive form card component'
      );
    }
  }

  scrollIntoViewIfEnabled() {
    if (this.config.scrollIntoViewWhenActive()) {
      const offset = this.scrollService.getElementVerticalOffset(
        this.elementRef.nativeElement
      );

      if (typeof offset !== 'undefined') {
        this.viewportScroller.scrollToPosition([0, offset]);
      }
    }
  }
}
